import { BrowserRouter as Router, Routes, Route, useParams, useLocation, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { MainLayout } from './components/layout/MainLayout';
import { ChatArea } from './components/layout/ChatArea';
import { FolderView } from './components/layout/FolderView';
import { CategoryFoldersView } from './components/layout/CategoryFoldersView';
import { HistoryView } from './components/layout/HistoryView';
import { SupplyPage } from './modules/supply/pages/SupplyPage';
import { SupplyAgentRulesPage } from './modules/supply/pages/SupplyAgentRulesPage';
import { OrdersPage } from './modules/orders/pages/OrdersPage';
import { BusinessAssistantPage } from './modules/business-rules/pages/BusinessAssistantPage';
import { ComplianceMonitorPage } from './modules/complianceMonitor/pages/ComplianceMonitorPage';
import { CustomerFulfillmentPage } from './modules/customerFulfillment/pages/CustomerFulfillmentPage';
import { OrderTrackingPage } from './modules/orderTracking/pages/orderTracking';
import { ForecastModuleLayout } from './modules/forecast-review/components/ForecastModuleLayout';
import { ForecastReviewPage } from './modules/forecast-review/pages/ForecastReviewPage';
import { ForecastDataValidationPage } from './modules/forecast-review/pages/ForecastDataValidationPage';
import { DemandPlanningSubrouteRedirect } from './modules/demand-planning/components/DemandPlanningSubrouteRedirect';
import { DemandPlanningModuleGate } from './modules/demand-planning/components/DemandPlanningModuleGate';
import { SopModuleLayout } from './modules/demand-planning/components/SopModuleLayout';
import { DemandPlanningHomePage } from './modules/demand-planning/pages/DemandPlanningHomePage';
import { ConsensusPage } from './modules/demand-planning/pages/ConsensusPage';
import { AssumptionsRedirect } from './modules/demand-planning/components/AssumptionsRedirect';
import { SopCyclesRedirect } from './modules/demand-planning/components/SopCyclesRedirect';
import { PlanningCyclesPage } from './modules/demand-planning/pages/PlanningCyclesPage';
import { ScenariosPage } from './modules/demand-planning/pages/ScenariosPage';
import { ForecastPage } from './modules/demand-planning/pages/ForecastPage';
import { ForecastAnomaliesPage } from './modules/demand-planning/pages/ForecastAnomaliesPage';
import { InventoryPolicies } from './pages/InventoryPolicies';
import { InventoryPolicyControlPage } from './modules/inventoryPolicyControl/pages/InventoryPolicyControlPage';
import { ProblemInsightsPage } from './modules/inventoryPolicyControl/pages/ProblemInsightsPage';
import { InventorySimulationPage } from './modules/inventorySimulation/pages/InventorySimulationPage';
import { NetworkReplenishmentModuleLayout } from './modules/networkReplenishment/components/NetworkReplenishmentModuleLayout';
import { NetworkReplenishmentTray } from './modules/networkReplenishment/components/NetworkReplenishmentTray';
import { NetworkReplenishmentConfigPage } from './modules/networkReplenishment/pages/NetworkReplenishmentConfigPage';
import { SafetyStockModuleLayout } from './modules/safetyStock/components/SafetyStockModuleLayout';
import { SafetyStockSummaryPage } from './modules/safetyStock/pages/SafetyStockSummaryPage';
import { SafetyStockParametersPage } from './modules/safetyStock/pages/SafetyStockParametersPage';
import { SafetyStockProductsPage } from './modules/safetyStock/pages/SafetyStockProductsPage';
import { UsageMonitoringModuleLayout } from './modules/usageMonitoring/components/UsageMonitoringModuleLayout';
import { UsageMonitoringOverviewPage } from './modules/usageMonitoring/pages/UsageMonitoringOverviewPage';
import { UsageMonitoringDetailPage } from './modules/usageMonitoring/pages/UsageMonitoringDetailPage';

import { Login } from './pages/Login';
import { Settings } from './pages/Settings';
import { ProductMasterListPage, ProductMasterDetailPage } from './modules/productMaster/ProductMasterPages';
import { EquivalencesPage } from './modules/equivalences/EquivalencesPage';
import { LogisticsCatalogPage } from './modules/logisticsCatalog/LogisticsCatalogPage';
import { HomePage } from './modules/home/pages/HomePage';
import { WarehouseProvider } from './contexts/WarehouseContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { AuthProvider } from './contexts/AuthContext';
import { FeatureVisibilityProvider, useFeatureVisibility } from './contexts/FeatureVisibilityContext';
import { AnalysisProvider } from './contexts/AnalysisContext';
import { ChatHeaderProvider } from './contexts/ChatHeaderContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import type { SupplyChatContext } from './services/copilotService';

function ChatAreaRoute() {
  const location = useLocation();
  
  // Verificar feature flag para AI Chat
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  
  // Si la feature flag está desactivada, redirigir a Home
  if (!isAIChatEnabled) {
    return <Navigate to="/home" replace />;
  }
  
  const state = location.state as {
    defaultFolderId?: string;
    copilotFromSupply?: boolean;
    supplyTableData?: unknown[];
    supplierName?: string;
  } | null;
  const defaultFolderId = state?.defaultFolderId ?? null;
  const supplyCopilotState: SupplyChatContext | null =
    state?.copilotFromSupply && Array.isArray(state?.supplyTableData)
      ? { supplyTableData: state.supplyTableData, supplierName: state.supplierName }
      : null;
  return (
    <ChatArea
      defaultFolderId={defaultFolderId}
      supplyCopilotState={supplyCopilotState}
    />
  );
}

function ChatAreaWithIdRoute() {
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  
  // Verificar feature flag para AI Chat
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  
  // Si la feature flag está desactivada, redirigir a Home
  if (!isAIChatEnabled) {
    return <Navigate to="/home" replace />;
  }
  
  const state = location.state as { supplyTableData?: unknown[]; supplierName?: string } | null;
  const supplyCopilotState: SupplyChatContext | null =
    state?.supplyTableData && Array.isArray(state.supplyTableData)
      ? { supplyTableData: state.supplyTableData, supplierName: state.supplierName }
      : null;
  return (
    <ChatArea
      chatId={chatId ?? null}
      supplyCopilotState={supplyCopilotState}
    />
  );
}

function ProtectedCategoryFoldersView() {
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  
  if (!isAIChatEnabled) {
    return <Navigate to="/home" replace />;
  }
  
  return <CategoryFoldersView />;
}

function ProtectedFolderView() {
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  
  if (!isAIChatEnabled) {
    return <Navigate to="/home" replace />;
  }
  
  return <FolderView />;
}

function ProtectedHistoryView() {
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  
  if (!isAIChatEnabled) {
    return <Navigate to="/home" replace />;
  }
  
  return <HistoryView />;
}

function FeatureRoute({
  route,
  fallback = '/supply',
  defaultEnabled = true,
  children,
}: {
  route: string;
  fallback?: string;
  defaultEnabled?: boolean;
  children: ReactNode;
}) {
  const { isRouteEnabled, loading } = useFeatureVisibility();
  // Wait until visibility config resolves; otherwise defaultEnabled=false routes
  // (e.g. /safety-stock) redirect to /supply on every hard refresh.
  if (loading) return null;
  if (!isRouteEnabled(route, defaultEnabled)) {
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function ActionRoute({
  action,
  fallback = '/supply',
  defaultEnabled = true,
  children,
}: {
  action: string;
  fallback?: string;
  defaultEnabled?: boolean;
  children: ReactNode;
}) {
  const { isActionEnabled, loading } = useFeatureVisibility();
  if (loading) return null;
  if (!isActionEnabled(action, defaultEnabled)) {
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <FeatureVisibilityProvider>
        <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
          <Router>
            <GoogleAnalytics />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute />}>
              <Route element={
                <WarehouseProvider>
                  <NotificationsProvider>
                    <AnalysisProvider>
                      <ChatHeaderProvider>
                        <MainLayout />
                      </ChatHeaderProvider>
                    </AnalysisProvider>
                  </NotificationsProvider>
                </WarehouseProvider>
              }>
                <Route index element={<ChatAreaRoute />} />
                <Route path="home" element={<HomePage />} />
                <Route path="analysis/category/:categoryId" element={<ProtectedCategoryFoldersView />} />
                <Route path="analysis/folder/:folderId" element={<ProtectedFolderView />} />
                <Route path="history" element={<ProtectedHistoryView />} />
                <Route path="analysis/chat/:chatId" element={<ChatAreaWithIdRoute />} />
                <Route path="supply" element={<FeatureRoute route="/supply"><SupplyPage /></FeatureRoute>} />
                <Route
                  path="supply/rules"
                  element={
                    <FeatureRoute route="/supply">
                      <SupplyAgentRulesPage />
                    </FeatureRoute>
                  }
                />
                <Route path="orders" element={<FeatureRoute route="/orders"><OrdersPage /></FeatureRoute>} />
                <Route path="customer-fulfillment" element={<FeatureRoute route="/customer-fulfillment"><CustomerFulfillmentPage /></FeatureRoute>} />
                <Route path="business-rules" element={<FeatureRoute route="/business-rules"><BusinessAssistantPage /></FeatureRoute>} />
                <Route path="compliance-monitor" element={<FeatureRoute route="/compliance-monitor"><ComplianceMonitorPage /></FeatureRoute>} />
                <Route path="order-tracking" element={<FeatureRoute route="/order-tracking"><OrderTrackingPage /></FeatureRoute>} />
                <Route path="sop" element={<Navigate to="/forecast" replace />} />
                <Route
                  path="forecast"
                  element={
                    <FeatureRoute route="/forecast">
                      <ForecastModuleLayout />
                    </FeatureRoute>
                  }
                >
                  <Route index element={<Navigate to="/forecast/review" replace />} />
                  <Route path="review" element={<ForecastReviewPage />} />
                  <Route
                    path="data-validation"
                    element={
                      <ActionRoute
                        action="forecast.review_data_validation"
                        defaultEnabled={false}
                        fallback="/forecast/review"
                      >
                        <ForecastDataValidationPage />
                      </ActionRoute>
                    }
                  />
                </Route>
                <Route
                  path="demand-planning"
                  element={
                    <DemandPlanningModuleGate>
                      <FeatureRoute route="/demand-planning" defaultEnabled={true}>
                        <DemandPlanningHomePage />
                      </FeatureRoute>
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/sop"
                  element={
                    <DemandPlanningModuleGate>
                      <FeatureRoute route="/demand-planning/sop" defaultEnabled={true}>
                        <SopModuleLayout />
                      </FeatureRoute>
                    </DemandPlanningModuleGate>
                  }
                >
                  <Route index element={<DemandPlanningSubrouteRedirect to="/demand-planning/sop/plan" />} />
                  <Route path="plan" element={<ConsensusPage embedded />} />
                  <Route path="cycles" element={<PlanningCyclesPage embedded />} />
                  <Route path="scenarios" element={<ScenariosPage embedded />} />
                </Route>
                <Route
                  path="demand-planning/scenarios"
                  element={
                    <DemandPlanningModuleGate>
                      <DemandPlanningSubrouteRedirect to="/demand-planning/sop/scenarios" />
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/consensus"
                  element={
                    <DemandPlanningModuleGate>
                      <DemandPlanningSubrouteRedirect to="/demand-planning/sop/plan" />
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/planning-cycles"
                  element={
                    <DemandPlanningModuleGate>
                      <DemandPlanningSubrouteRedirect to="/demand-planning/sop/cycles" />
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/forecast"
                  element={
                    <DemandPlanningModuleGate>
                      <FeatureRoute route="/demand-planning/forecast" defaultEnabled={true}>
                        <ForecastPage />
                      </FeatureRoute>
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/forecast/anomalies"
                  element={
                    <DemandPlanningModuleGate>
                      <FeatureRoute route="/demand-planning/forecast" defaultEnabled={true}>
                        <ForecastAnomaliesPage />
                      </FeatureRoute>
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/sop-cycles"
                  element={
                    <DemandPlanningModuleGate>
                      <SopCyclesRedirect />
                    </DemandPlanningModuleGate>
                  }
                />
                <Route
                  path="demand-planning/assumptions"
                  element={
                    <DemandPlanningModuleGate>
                      <AssumptionsRedirect />
                    </DemandPlanningModuleGate>
                  }
                />
                <Route path="inventory-policy-control" element={<FeatureRoute route="/inventory-policy-control" defaultEnabled={false}><InventoryPolicyControlPage /></FeatureRoute>} />
                <Route path="inventory-policy-control/problems" element={<FeatureRoute route="/inventory-policy-control/problems" defaultEnabled={false}><ProblemInsightsPage /></FeatureRoute>} />
                <Route path="inventory-policies" element={<FeatureRoute route="/inventory-policies"><InventoryPolicies /></FeatureRoute>} />
                <Route path="inventory-simulation" element={<FeatureRoute route="/inventory-simulation" defaultEnabled={false}><InventorySimulationPage /></FeatureRoute>} />
                <Route
                  path="network-replenishment"
                  element={
                    <FeatureRoute route="/network-replenishment" defaultEnabled={false}>
                      <NetworkReplenishmentModuleLayout />
                    </FeatureRoute>
                  }
                >
                  <Route index element={<Navigate to="/network-replenishment/tray" replace />} />
                  <Route path="tray" element={<NetworkReplenishmentTray />} />
                  <Route path="config" element={<NetworkReplenishmentConfigPage />} />
                </Route>
                <Route
                  path="safety-stock"
                  element={
                    <FeatureRoute route="/safety-stock" defaultEnabled={false}>
                      <SafetyStockModuleLayout />
                    </FeatureRoute>
                  }
                >
                  <Route index element={<Navigate to="/safety-stock/resumen" replace />} />
                  <Route path="resumen" element={<SafetyStockSummaryPage />} />
                  <Route path="parametros" element={<SafetyStockParametersPage />} />
                  <Route path="productos" element={<SafetyStockProductsPage />} />
                  {/* Recálculos oculto de momento */}
                  <Route path="recalculos" element={<Navigate to="/safety-stock/resumen" replace />} />
                </Route>
                <Route
                  path="usage-monitoring"
                  element={
                    <FeatureRoute route="/usage-monitoring" defaultEnabled={false}>
                      <UsageMonitoringModuleLayout />
                    </FeatureRoute>
                  }
                >
                  <Route index element={<Navigate to="/usage-monitoring/resumen" replace />} />
                  <Route path="resumen" element={<UsageMonitoringOverviewPage />} />
                  <Route path="usuarios" element={<Navigate to="/usage-monitoring/resumen" replace />} />
                  <Route path="detalle" element={<UsageMonitoringDetailPage />} />
                </Route>
                <Route path="settings" element={<FeatureRoute route="/settings"><Settings /></FeatureRoute>} />
                <Route
                  path="settings/products"
                  element={
                    <FeatureRoute route="/settings/products" defaultEnabled={false}>
                      <ProductMasterListPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="settings/products/:productId"
                  element={
                    <FeatureRoute route="/settings/products" defaultEnabled={false}>
                      <ProductMasterDetailPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="settings/equivalences"
                  element={
                    <FeatureRoute route="/settings/equivalences" defaultEnabled={false}>
                      <EquivalencesPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="settings/logistics"
                  element={
                    <FeatureRoute route="/settings/logistics" defaultEnabled={false}>
                      <LogisticsCatalogPage />
                    </FeatureRoute>
                  }
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
          </Router>
        </div>
      </FeatureVisibilityProvider>
    </AuthProvider>
  );
}

export default App;
